import React, { useState } from "react";

import { useTranslation } from "utils";

import { Flex, Icon, Popover, Tag, Typography } from "components";

// same visual pattern (and class names) as the FilterChip/FilterOption used by FieldFilterOptionsChips.tsx on
// Timeline, duplicated here rather than imported — same reasoning as AllFieldsDataStatusFilter.tsx: that file's
// version is tightly coupled to its own server-driven crop/year filtering, and this is a single-select year picker
// rather than the multi-select "years" filter Timeline offers.
export const AllFieldsHarvestYearFilter = ({
  availableYears,
  selectedYear,
  onSelectYear,
  isDisabled,
  yearsWithMissingData,
}: {
  availableYears: number[];
  selectedYear: number | undefined;
  onSelectYear: (year: number) => void;
  isDisabled: boolean;
  yearsWithMissingData: Set<number>;
}) => {
  const { t } = useTranslation("allFieldsDataTableView");
  const [isOpen, setIsOpen] = useState(false);
  // the year switch starves the popover's fade-out animation, so after a pick it is hidden at once
  const [isHiddenAfterSelect, setIsHiddenAfterSelect] = useState(false);

  const handleSelect = (year: number) => {
    setIsHiddenAfterSelect(true);
    setIsOpen(false);
    onSelectYear(year);
  };

  return (
    <Flex className="field-filter-chip">
      <Popover
        open={isOpen}
        onOpenChange={(open) => {
          if (isDisabled) return;
          if (open) setIsHiddenAfterSelect(false);
          setIsOpen(open);
        }}
        trigger="click"
        placement="bottomLeft"
        className={`filter-chip-popover${isHiddenAfterSelect ? " all-fields-harvest-year-popover-hidden" : ""}`}
        content={
          <Flex vertical className="filter-chip-popover-content all-fields-harvest-year-options" data-testid="all-fields-harvest-year-checkboxes-container">
            {availableYears.map((year) => (
              <Flex
                key={year}
                className={`filter-chip-option${selectedYear === year ? " selected" : ""}`}
                justify="space-between"
                align="center"
                role="button"
                tabIndex={0}
                data-testid={`all-fields-harvest-year-checkbox-${year}`}
                onClick={() => handleSelect(year)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(year);
                  }
                }}
              >
                <Flex align="center" gap="0.375rem">
                  <Typography variant="paragraph-m" className="margin-0">
                    {year.toString()}
                  </Typography>
                  {yearsWithMissingData.has(year) && (
                    <span className="all-fields-harvest-year-missing-dot" data-testid={`all-fields-harvest-year-missing-dot-${year}`} />
                  )}
                </Flex>
                {selectedYear === year && <Icon iconName="tabler/circle-check" size="m-light" className="filter-chip-option-check" />}
              </Flex>
            ))}
          </Flex>
        }
      >
        <Flex className="field-filter-chip-trigger" align="center" gap="0.5rem" role="button" tabIndex={0}>
          <Typography variant="paragraph-m" noWrap className="margin-0">
            {t("allFieldsDataTableView:filters.harvestYear")}
          </Typography>
          {selectedYear !== undefined && yearsWithMissingData.has(selectedYear) && (
            <span className="all-fields-harvest-year-missing-dot" data-testid="all-fields-harvest-year-missing-dot-summary" />
          )}
          {selectedYear && <Tag colour="green-300" className="all-fields-harvest-year-tag" text={selectedYear.toString()} />}
          <Icon iconName="tabler/chevron-down" size="s" />
        </Flex>
      </Popover>
    </Flex>
  );
};
